/**
 * CSVImportDialog Component
 * Import dialog for CSV/Excel data:
 * - File input or paste text
 * - Delimiter detection (comma, tab, semicolon)
 * - First row as header option
 * - Preview before import
 */

import { useState, useCallback, useRef, ChangeEvent } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Upload,
  FileText,
  ClipboardPaste,
  AlertCircle,
  CheckCircle2,
  Table as TableIcon,
} from 'lucide-react'
import type { TableHeader, TableRow, TableCell } from '@/lib/api/types'

// ============================================
// Types
// ============================================

interface CSVImportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onImport: (headers: TableHeader[], rows: TableRow[]) => void
}

type Delimiter = ',' | '\t' | ';' | '|'

interface ParseResult {
  headers: string[]
  rows: string[][]
  error?: string
}

// ============================================
// Constants
// ============================================

const DELIMITERS: { value: Delimiter; label: string }[] = [
  { value: ',', label: 'Comma (,)' },
  { value: '\t', label: 'Tab' },
  { value: ';', label: 'Semicolon (;)' },
  { value: '|', label: 'Pipe (|)' },
]

// ============================================
// Helper Functions
// ============================================

/**
 * Detect delimiter from CSV content
 */
function detectDelimiter(content: string): Delimiter {
  const firstLine = content.split('\n')[0] || ''

  const counts: Record<Delimiter, number> = {
    ',': (firstLine.match(/,/g) || []).length,
    '\t': (firstLine.match(/\t/g) || []).length,
    ';': (firstLine.match(/;/g) || []).length,
    '|': (firstLine.match(/\|/g) || []).length,
  }

  let maxCount = 0
  let detected: Delimiter = ','

  for (const [delimiter, count] of Object.entries(counts) as [Delimiter, number][]) {
    if (count > maxCount) {
      maxCount = count
      detected = delimiter
    }
  }

  return detected
}

/**
 * Parse CSV content with proper quote handling
 */
function parseCSV(content: string, delimiter: Delimiter): string[][] {
  const rows: string[][] = []
  const lines = content.split(/\r?\n/)

  for (const line of lines) {
    if (!line.trim()) continue

    const row: string[] = []
    let cell = ''
    let inQuotes = false

    for (let i = 0; i < line.length; i++) {
      const char = line[i]
      const nextChar = line[i + 1]

      if (inQuotes) {
        if (char === '"') {
          if (nextChar === '"') {
            // Escaped quote
            cell += '"'
            i++
          } else {
            // End of quoted section
            inQuotes = false
          }
        } else {
          cell += char
        }
      } else {
        if (char === '"') {
          inQuotes = true
        } else if (char === delimiter) {
          row.push(cell.trim())
          cell = ''
        } else {
          cell += char
        }
      }
    }

    // Push the last cell
    row.push(cell.trim())
    rows.push(row)
  }

  return rows
}

/**
 * Generate unique column key
 */
function generateColumnKey(): string {
  return `col_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
}

/**
 * Generate unique row ID
 */
function generateRowId(): string {
  return `row_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
}

/**
 * Convert parsed CSV to table data
 */
function convertToTableData(
  parsed: string[][],
  firstRowIsHeader: boolean
): { headers: TableHeader[]; rows: TableRow[] } {
  if (parsed.length === 0) {
    return { headers: [], rows: [] }
  }

  const firstRow = parsed[0] || []
  const columnCount = Math.max(...parsed.map((row) => row.length))

  // Create headers
  const headers: TableHeader[] = Array.from({ length: columnCount }, (_, i) => ({
    key: generateColumnKey(),
    label: firstRowIsHeader && firstRow[i] ? firstRow[i] : `Column ${i + 1}`,
    dataType: 'Text' as const,
    width: 150,
    alignment: 'Left' as const,
  }))

  // Create rows (skip first row if it's the header)
  const dataRows = firstRowIsHeader ? parsed.slice(1) : parsed
  const rows: TableRow[] = dataRows.map((rowData, rowIndex) => {
    const cells: Record<string, TableCell> = {}
    headers.forEach((header, colIndex) => {
      cells[header.key] = {
        value: rowData[colIndex] || '',
        displayValue: rowData[colIndex] || '',
      }
    })
    return {
      id: generateRowId(),
      cells,
      order: rowIndex,
    }
  })

  return { headers, rows }
}

// ============================================
// Preview Table Component
// ============================================

interface PreviewTableProps {
  headers: string[]
  rows: string[][]
  maxRows?: number
}

function PreviewTable({ headers, rows, maxRows = 5 }: PreviewTableProps) {
  const displayRows = rows.slice(0, maxRows)
  const hasMore = rows.length > maxRows

  return (
    <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden">
      <div className="overflow-auto max-h-[200px]">
        <table className="w-full text-sm">
          <thead className="bg-neutral-100 dark:bg-neutral-800">
            <tr>
              {headers.map((header, i) => (
                <th
                  key={i}
                  className="px-3 py-2 text-left font-medium text-neutral-700 dark:text-neutral-300 border-b border-neutral-200 dark:border-neutral-700"
                >
                  {header || <span className="text-neutral-400">Column {i + 1}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, rowIndex) => (
              <tr key={rowIndex} className="border-b border-neutral-200 dark:border-neutral-700 last:border-0">
                {headers.map((_, colIndex) => (
                  <td
                    key={colIndex}
                    className="px-3 py-2 text-neutral-600 dark:text-neutral-400"
                  >
                    {row[colIndex] || '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasMore && (
        <div className="px-3 py-2 text-xs text-neutral-500 bg-neutral-50 dark:bg-neutral-800/50 border-t border-neutral-200 dark:border-neutral-700">
          Showing {displayRows.length} of {rows.length} rows
        </div>
      )}
    </div>
  )
}

// ============================================
// CSVImportDialog Component
// ============================================

export function CSVImportDialog({
  open,
  onOpenChange,
  onImport,
}: CSVImportDialogProps) {
  const [inputMethod, setInputMethod] = useState<'file' | 'paste'>('file')
  const [csvContent, setCsvContent] = useState('')
  const [delimiter, setDelimiter] = useState<Delimiter>(',')
  const [firstRowIsHeader, setFirstRowIsHeader] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Parse CSV content
  const parseResult = useCallback((): ParseResult => {
    if (!csvContent.trim()) {
      return { headers: [], rows: [], error: 'No content to parse' }
    }

    try {
      const parsed = parseCSV(csvContent, delimiter)
      if (parsed.length === 0) {
        return { headers: [], rows: [], error: 'No valid data found' }
      }

      const firstRow = parsed[0] || []
      const headers = firstRowIsHeader
        ? firstRow.map((h, i) => h || `Column ${i + 1}`)
        : firstRow.map((_, i) => `Column ${i + 1}`)
      const rows = firstRowIsHeader ? parsed.slice(1) : parsed

      return { headers, rows }
    } catch (err) {
      return {
        headers: [],
        rows: [],
        error: err instanceof Error ? err.message : 'Failed to parse CSV',
      }
    }
  }, [csvContent, delimiter, firstRowIsHeader])

  const result = parseResult()

  // Handle file upload
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      const content = event.target?.result as string
      setCsvContent(content)
      // Auto-detect delimiter
      setDelimiter(detectDelimiter(content))
      setError(null)
    }
    reader.onerror = () => {
      setError('Failed to read file')
    }
    reader.readAsText(file)
  }

  // Handle paste
  const handlePaste = (content: string) => {
    setCsvContent(content)
    // Auto-detect delimiter
    if (content.trim()) {
      setDelimiter(detectDelimiter(content))
    }
    setError(null)
  }

  // Handle import
  const handleImport = () => {
    if (result.error || result.rows.length === 0) {
      setError(result.error || 'No data to import')
      return
    }

    const { headers, rows } = convertToTableData(
      firstRowIsHeader
        ? [result.headers, ...result.rows]
        : result.rows.map((row) => [...row]),
      firstRowIsHeader
    )

    onImport(headers, rows)
    handleReset()
  }

  // Reset state
  const handleReset = () => {
    setCsvContent('')
    setDelimiter(',')
    setFirstRowIsHeader(true)
    setError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Handle dialog close
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleReset()
    }
    onOpenChange(open)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Import CSV Data
          </DialogTitle>
          <DialogDescription>
            Upload a CSV file or paste data directly to import into your table.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Input Method Tabs */}
          <Tabs value={inputMethod} onValueChange={(v) => setInputMethod(v as 'file' | 'paste')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="file" className="gap-2">
                <FileText className="h-4 w-4" />
                Upload File
              </TabsTrigger>
              <TabsTrigger value="paste" className="gap-2">
                <ClipboardPaste className="h-4 w-4" />
                Paste Data
              </TabsTrigger>
            </TabsList>

            <TabsContent value="file" className="mt-4">
              <div className="space-y-2">
                <Label>Select CSV File</Label>
                <div
                  className={cn(
                    'border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer',
                    'hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/10',
                    csvContent
                      ? 'border-green-400 bg-green-50 dark:bg-green-900/10'
                      : 'border-neutral-300 dark:border-neutral-600'
                  )}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.tsv,.txt"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  {csvContent ? (
                    <div className="flex items-center justify-center gap-2 text-green-600 dark:text-green-400">
                      <CheckCircle2 className="h-5 w-5" />
                      <span>File loaded successfully</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="h-8 w-8 mx-auto text-neutral-400" />
                      <p className="text-sm text-neutral-500">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-xs text-neutral-400">
                        CSV, TSV, or TXT files
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="paste" className="mt-4">
              <div className="space-y-2">
                <Label htmlFor="csv-paste">Paste CSV Data</Label>
                <Textarea
                  id="csv-paste"
                  placeholder="Paste your CSV data here...&#10;Name,Email,Phone&#10;John Doe,john@example.com,555-1234"
                  value={csvContent}
                  onChange={(e) => handlePaste(e.target.value)}
                  className="min-h-[150px] font-mono text-sm"
                />
              </div>
            </TabsContent>
          </Tabs>

          {/* Options */}
          <div className="flex items-center gap-6 pt-2">
            <div className="space-y-1.5 flex-1">
              <Label>Delimiter</Label>
              <Select value={delimiter} onValueChange={(v) => setDelimiter(v as Delimiter)}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DELIMITERS.map((d) => (
                    <SelectItem key={d.value} value={d.value}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3 pt-6">
              <Switch
                id="first-row-header"
                checked={firstRowIsHeader}
                onCheckedChange={setFirstRowIsHeader}
              />
              <Label htmlFor="first-row-header" className="text-sm cursor-pointer">
                First row is header
              </Label>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Preview */}
          {csvContent && !result.error && result.rows.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <TableIcon className="h-4 w-4" />
                  Preview
                </Label>
                <span className="text-xs text-neutral-500">
                  {result.headers.length} columns, {result.rows.length} rows
                </span>
              </div>
              <PreviewTable headers={result.headers} rows={result.rows} />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={!csvContent || result.error !== undefined || result.rows.length === 0}
          >
            <Upload className="h-4 w-4 mr-2" />
            Import {result.rows.length > 0 ? `${result.rows.length} rows` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CSVImportDialog
